'use client'

import Link from 'next/link'

import { EditableText } from '@/components/content-edit/editable-text'
import { Button } from '@/components/ui/button'
import { V2Hero } from '@/components/v2/v2-hero'

import type { HomeStatsCopy } from './stat-section'

export interface HomeHeroCopy {
  eyebrow: string
  titleLine1: string
  titleLine2: string
  titleAccent: string
  subtitle: string
  primaryCta: string
  secondaryCta: string
}

export function HeroSection({ copy, stats }: { copy: HomeHeroCopy; stats: HomeStatsCopy }) {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <V2Hero
      dark={false}
      eyebrow={
        <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.eyebrow' }} value={copy.eyebrow} />
      }
      title={
        <>
          <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.titleLine1' }} value={copy.titleLine1} />
          <br />
          <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.titleLine2' }} value={copy.titleLine2} />
          <br />
          {/* Bright teal is allowed here because it's 88px display type
              (clears the 3:1 headline allowance); do not reuse this colour
              for text at any smaller size (design-system brand-recolour
              handoff, 2026-09-08). */}
          <EditableText
            source={{ kind: 'copy', file: 'home', path: 'hero.titleAccent' }}
            value={copy.titleAccent}
            className="text-[var(--cw-lime)]"
          />
        </>
      }
      subtitle={
        <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.subtitle' }} value={copy.subtitle} />
      }
      actions={
        <>
          <Link href="/fleet-quote">
            <Button variant="primary" size="md" className="px-6 font-semibold">
              <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.primaryCta' }} value={copy.primaryCta} />
            </Button>
          </Link>
          <Button
            variant="outline"
            size="md"
            className="px-6"
            onPress={() => scrollToSection('portfolio')}
          >
            <EditableText source={{ kind: 'copy', file: 'home', path: 'hero.secondaryCta' }} value={copy.secondaryCta} />
          </Button>
        </>
      }
      mediaBelow
      media={
        <video
          src="/main.webm"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          className="h-[clamp(240px,42vw,430px)] w-full rounded-3xl object-cover"
        />
      }
      stats={[
        {
          value: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s1.value' }} value={stats.s1.value} />
          ),
          label: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s1.label' }} value={stats.s1.label} />
          ),
        },
        {
          value: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s2.value' }} value={stats.s2.value} />
          ),
          label: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s2.label' }} value={stats.s2.label} />
          ),
        },
        {
          value: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s3.value' }} value={stats.s3.value} />
          ),
          label: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s3.label' }} value={stats.s3.label} />
          ),
        },
        {
          value: (
            <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s4.value' }} value={stats.s4.value} />
          ),
          // The film manufacturer sets this term, not us, and it varies by
          // product line. The link goes to the page that says so, so a headline
          // number cannot read as a promise we made.
          label: (
            <Link href="/warranty" className="underline underline-offset-4 decoration-1">
              <EditableText source={{ kind: 'copy', file: 'home', path: 'stats.s4.label' }} value={stats.s4.label} />
            </Link>
          ),
        },
      ]}
    />
  )
}
